import { getDownloadURL, ref } from "firebase/storage";
import { storage, getFirebaseAuthToken, hasCustomClaims } from "../firebase/firebase";
import { initDuckDB } from "./duckdbLoader";
import { getSyncTenpoCodesPromise, syncTenpoCodes, getUserInfo } from "../data/userInfo";
import type { AsyncDuckDB } from "@duckdb/duckdb-wasm";

const PARQUET_API_URL = process.env.REACT_APP_PARQUET_API_URL;

const downloadCache: Record<string, Promise<Uint8Array>> = {};
let cachedTenpoItemData: { storePath: string; data: any[] } | null = null;
const clusterNoCache: Record<string, string> = (() => {
  try {
    const saved = sessionStorage.getItem('clusterNoCache');
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
})();
const signedUrlCache: Record<string, { url: string; expires: number }> = {};
let latestCallId = 0;
let previousFiles: string[] = [];

const STORE_CD_MAP_PATH = 'latest_store_cd_map/store_cd_map.parquet';
let storeCdMapCache: Record<string, string> | null = null;
let storeCdMapPromise: Promise<Record<string, string>> | null = null;

const ensureSyncTenpoCodes = (): Promise<void> => {
  const existing = getSyncTenpoCodesPromise();
  if (existing) return existing;
  return syncTenpoCodes();
};

const SIGNED_URL_TTL = 12 * 60 * 1000;

const batchGetSignedUrls = async (gcsPaths: string[]): Promise<void> => {
  if (!PARQUET_API_URL) return;
  const uncached = gcsPaths.filter((p) => {
    const c = signedUrlCache[p];
    return !c || Date.now() >= c.expires;
  });
  if (uncached.length === 0) return;

  const claimsExist = await hasCustomClaims();
  let token: string | undefined;

  if (claimsExist) {
    token = await getFirebaseAuthToken(false);
    ensureSyncTenpoCodes().catch(() => {});
  } else {
    console.time("[gcsDataLoader] syncTenpoCodes待ち");
    await ensureSyncTenpoCodes();
    console.timeEnd("[gcsDataLoader] syncTenpoCodes待ち");
    token = await getFirebaseAuthToken(true);
  }

  console.time("[gcsDataLoader] バッチ署名付きURL取得");
  const params = uncached.map((p) => `paths=${encodeURIComponent(p)}`).join("&");
  let res = await fetch(
    `${PARQUET_API_URL}/getSignedUrls?${params}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok && res.status === 403 && claimsExist) {
    await ensureSyncTenpoCodes();
    token = await getFirebaseAuthToken(true);
    res = await fetch(
      `${PARQUET_API_URL}/getSignedUrls?${params}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  }
  if (!res.ok) {
    const errorBody = await res.text();
    console.error("[gcsDataLoader] バッチ署名付きURL取得エラー:", res.status, errorBody);
    throw new Error(`バッチ署名付きURL取得エラー: ${res.status} ${errorBody}`);
  }
  const data = await res.json();
  const now = Date.now();
  for (const [path, url] of Object.entries(data.urls as Record<string, string>)) {
    signedUrlCache[path] = { url, expires: now + SIGNED_URL_TTL };
  }
  console.timeEnd("[gcsDataLoader] バッチ署名付きURL取得");
};

const getSignedUrl = async (gcsPath: string): Promise<string> => {
  if (PARQUET_API_URL) {
    const cached = signedUrlCache[gcsPath];
    if (cached && Date.now() < cached.expires) {
      return cached.url;
    }
    const claimsExist = await hasCustomClaims();
    let token: string | undefined;
    if (claimsExist) {
      token = await getFirebaseAuthToken(false);
      ensureSyncTenpoCodes().catch(() => {});
    } else {
      await ensureSyncTenpoCodes();
      token = await getFirebaseAuthToken(true);
    }
    let res = await fetch(
      `${PARQUET_API_URL}/getSignedUrl?path=${encodeURIComponent(gcsPath)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok && res.status === 403 && claimsExist) {
      await ensureSyncTenpoCodes();
      token = await getFirebaseAuthToken(true);
      res = await fetch(
        `${PARQUET_API_URL}/getSignedUrl?path=${encodeURIComponent(gcsPath)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    }
    if (!res.ok) {
      const errorBody = await res.text();
      console.error("[gcsDataLoader] 署名付きURL取得エラー:", res.status, errorBody);
      throw new Error(`署名付きURL取得エラー: ${res.status} ${errorBody}`);
    }
    const data = await res.json();
    signedUrlCache[gcsPath] = { url: data.url, expires: Date.now() + SIGNED_URL_TTL };
    return data.url;
  }
  const fileRef = ref(storage, gcsPath);
  return getDownloadURL(fileRef);
};

const downloadParquet = (gcsPath: string): Promise<Uint8Array> => {
  if (gcsPath in downloadCache) return downloadCache[gcsPath];
  downloadCache[gcsPath] = (async () => {
    const url = await getSignedUrl(gcsPath);
    const response = await fetch(url);
    if (!response.ok) {
      delete downloadCache[gcsPath];
      throw new Error(`Parquetダウンロード失敗: ${gcsPath} (${response.status})`);
    }
    return new Uint8Array(await response.arrayBuffer());
  })();
  return downloadCache[gcsPath];
};

const loadStoreCdMap = async (): Promise<Record<string, string>> => {
  if (storeCdMapCache) return storeCdMapCache;
  if (storeCdMapPromise) return storeCdMapPromise;

  storeCdMapPromise = (async () => {
    console.time("[gcsDataLoader] store_cd_mapロード");
    const db = await initDuckDB();
    const buffer = await downloadParquet(STORE_CD_MAP_PATH);
    const fileName = `store_cd_map_${Date.now()}.parquet`;
    await db.registerFileBuffer(fileName, buffer.slice());
    const conn = await db.connect();
    try {
      const result = await conn.query(`
        SELECT
          TRIM(CAST(old_store_cd AS VARCHAR)) AS old_store_cd,
          TRIM(CAST(org_store_cd AS VARCHAR)) AS org_store_cd
        FROM read_parquet('${fileName}')
      `);
      const map: Record<string, string> = {};
      for (const row of result.toArray()) {
        map[String(row.old_store_cd)] = String(row.org_store_cd);
      }
      storeCdMapCache = map;
      console.timeEnd("[gcsDataLoader] store_cd_mapロード");
      return map;
    } finally {
      await conn.close();
      await db.dropFile(fileName).catch(() => {});
    }
  })();

  return storeCdMapPromise;
};

export const resolveOrgStoreCd = async (oldStoreCd: string): Promise<string> => {
  const map = await loadStoreCdMap();
  return map[oldStoreCd] || oldStoreCd;
};

export const preloadStoreCdMap = (): void => {
  loadStoreCdMap().catch((e) =>
    console.error("[gcsDataLoader] store_cd_mapプリロード失敗:", e)
  );
};

const SORT_CONFIG: Record<
  string,
  { qualifyCol: string; rankAlias: string }
> = {
  store_avg_quantity_rank: {
    qualifyCol: "store_avg_daily_quantity",
    rankAlias: "sales_quantity_rank",
  },
  store_avg_sales_rank: {
    qualifyCol: "store_avg_daily_sales",
    rankAlias: "sales_amount_rank",
  },
  cluster_avg_quantity_rank: {
    qualifyCol: "cluster_avg_daily_quantity",
    rankAlias: "cluster_sales_quantity_rank",
  },
  cluster_avg_sales_rank: {
    qualifyCol: "cluster_avg_daily_sales",
    rankAlias: "cluster_sales_amount_rank",
  },
  do_avg_quantity_rank: {
    qualifyCol: "do_avg_daily_quantity",
    rankAlias: "do_sales_quantity_rank",
  },
  do_avg_sales_rank: {
    qualifyCol: "do_avg_daily_sales",
    rankAlias: "do_sales_amount_rank",
  },
};

interface FilterOptions {
  checkedInfo?: string[];
  tobaccoFlag: boolean;
  reservationFlag: boolean;
  specialFlag: boolean;
  smsFlag: boolean;
  dailyItemFlag: string;
  selectedRank: string;
  sortOrder: string;
}

export const fetchAssortmentAnalysisData = async (
  period: "weekly" | "monthly",
  tenpoCode: string,
  date: string,
  filters: FilterOptions,
  clusterNo?: string,
): Promise<{
  assortmentAnalysis: any[];
  tenpoItemData: any[];
  defaultCluster: string;
} | null> => {
  const callId = ++latestCallId;
  const tag = `[gcsDataLoader#${callId}]`;
  console.time(`${tag} 処理合計`);

  const orgStoreCd = await resolveOrgStoreCd(tenpoCode);

  if (orgStoreCd !== tenpoCode) {
    const currentUserInfo = getUserInfo();
    if (!currentUserInfo.tenpo_code.includes(orgStoreCd)) {
      const updatedTenpoCodes = [...currentUserInfo.tenpo_code, orgStoreCd];
      const updatedUserInfo = { ...currentUserInfo, tenpo_code: updatedTenpoCodes };
      sessionStorage.setItem("userInfo", JSON.stringify(updatedUserInfo));
      await syncTenpoCodes(updatedUserInfo);
    }
  }

  const storePath = `${period}/${date}/stores/${orgStoreCd}.parquet`;
  const stockPath = `latest_stock/stores/${tenpoCode}.parquet`;
  const cachedCluster = clusterNo || clusterNoCache[tenpoCode];

  const specClusterPath = cachedCluster
    ? `${period}/${date}/clusters/${cachedCluster.padStart(2, "0")}.parquet`
    : null;

  const allClusterPaths = !specClusterPath
    ? [
        ...Array.from({length: 9}, (_, i) =>
          `${period}/${date}/clusters/${String(i + 1).padStart(2, "0")}.parquet`
        ),
        ...Array.from({length: 7}, (_, i) =>
          `${period}/${date}/clusters/鉄道特殊${i + 1}.parquet`
        ),
      ]
    : [];
  const pathsToFetch = specClusterPath
    ? [storePath, specClusterPath, stockPath]
    : [storePath, stockPath, ...allClusterPaths];
  console.time(`${tag} 署名URL+認証待ち`);
  const batchUrlPromise = batchGetSignedUrls(pathsToFetch);
  const duckDbPromise = initDuckDB();

  await batchUrlPromise;
  console.timeEnd(`${tag} 署名URL+認証待ち`);

  console.time(`${tag} Parquetダウンロード`);
  const storeDownloadPromise = downloadParquet(storePath);
  const specClusterPromise = specClusterPath ? downloadParquet(specClusterPath) : null;
  const stockDownloadPromise = downloadParquet(stockPath);

  const [db, storeBuffer, stockBuffer] = await Promise.all([
    duckDbPromise, storeDownloadPromise, stockDownloadPromise,
  ]);
  console.timeEnd(`${tag} Parquetダウンロード`);

  if (callId !== latestCallId) {
    return null;
  }

  const storeFileName = `store_${callId}.parquet`;
  const stockFileName = `stock_${callId}.parquet`;
  await Promise.all([
    ...previousFiles.map(name => db.dropFile(name).catch(() => {})),
    db.registerFileBuffer(storeFileName, storeBuffer.slice()),
    db.registerFileBuffer(stockFileName, stockBuffer.slice()),
  ]);
  previousFiles = [storeFileName, stockFileName];

  // コネクションを作成し、default_cluster クエリ後も再利用する
  const conn = await db.connect();

  if (!clusterNo) {
    const storeInfo = await conn.query(
      `SELECT default_cluster FROM read_parquet('${storeFileName}') LIMIT 1`,
    );
    clusterNo = String(storeInfo.toArray()[0].default_cluster);
  }
  clusterNoCache[tenpoCode] = clusterNo;
  try { sessionStorage.setItem('clusterNoCache', JSON.stringify(clusterNoCache)); } catch {}
  const paddedCluster = clusterNo.padStart(2, "0");
  const clusterFileName = `cluster_${callId}_${paddedCluster}.parquet`;
  const clusterPath = `${period}/${date}/clusters/${paddedCluster}.parquet`;

  console.time(`${tag} cluster Parquet取得`);
  const clusterBuffer = (specClusterPromise && specClusterPath === clusterPath)
    ? await specClusterPromise
    : await downloadParquet(clusterPath);
  await db.registerFileBuffer(clusterFileName, clusterBuffer.slice());
  previousFiles.push(clusterFileName);
  console.timeEnd(`${tag} cluster Parquet取得`);

  if (callId !== latestCallId) {
    return null;
  }

  try {

    const storeWhere: string[] = [
      `CAST(store_cd AS VARCHAR) = '${orgStoreCd}'`,
      "recommendation IS NULL",
      "CAST(item_cd AS VARCHAR) NOT IN ('250899', '310464')",
      "tax_excluded_price != 0",
    ];
    if (filters.tobaccoFlag) {
      storeWhere.push("TRIM(CAST(pma AS VARCHAR)) != '61'");
    }
    if (filters.reservationFlag) {
      storeWhere.push("TRIM(CAST(reservation_item_class AS VARCHAR)) != '1'");
    }
    if (filters.specialFlag) {
      storeWhere.push("TRIM(CAST(special_item_class AS VARCHAR)) != '1'");
    }
    if (filters.smsFlag) {
      storeWhere.push("TRIM(CAST(sms_item_class AS VARCHAR)) NOT IN ('2')");
    }
    if (filters.checkedInfo && filters.checkedInfo.length > 0) {
      const conditions = filters.checkedInfo
        .map((item) => {
          const [pma, info] = item.split("_");
          return `(TRIM(CAST(pma AS VARCHAR)) = '${pma}' AND TRIM(CAST(information_class AS VARCHAR)) = '${info}')`;
        })
        .join(" OR ");
      storeWhere.push(`(${conditions})`);
    }

    const sortConfig =
      SORT_CONFIG[filters.selectedRank] || SORT_CONFIG["cluster_avg_sales_rank"];
    const sortDir = filters.sortOrder === "desc" ? "DESC" : "ASC";

    const mainSql = `
      WITH joined AS (
        SELECT
          s.store_cd,
          s.store_name,
          s.default_cluster,
          '${paddedCluster}' AS selected_cluster,
          s.do_cd,
          s.do_name,
          s.item_cd,
          s.item_name,
          s.pma,
          s.pma_name,
          s.information_class,
          s.information_class_name,
          s.tax_included_price,
          s.item_rank,
          s.order_start_date,
          s.order_end_date,
          stk.stock_amnt,
          stk.stock_amnt_datetime,
          s.has_wsi,
          s.store_new_item_flg,
          s.recommendation,
          s.store_avg_daily_quantity,
          s.store_avg_daily_sales,
          s.store_adoption_rate,
          s.store_out_of_stock_rate_hours,
          s.store_disposal_rate_quantity,
          s.do_avg_daily_quantity,
          s.do_avg_daily_sales,
          s.do_adoption_rate,
          s.do_out_of_stock_rate_hours,
          s.do_disposal_rate_quantity,
          c.cluster_avg_daily_quantity,
          c.cluster_avg_daily_sales,
          c.cluster_adoption_rate,
          c.cluster_out_of_stock_rate_hours,
          c.cluster_disposal_rate_quantity,
          c.cluster_avg_recommended_stores_per_day
        FROM (
          SELECT
            store_cd, store_name, default_cluster, do_cd, do_name,
            item_cd, item_name, pma, pma_name,
            information_class, information_class_name,
            tax_included_price, item_rank,
            order_start_date, order_end_date,
            has_wsi, store_new_item_flg, recommendation,
            store_avg_daily_quantity, store_avg_daily_sales,
            store_adoption_rate, store_out_of_stock_rate_hours,
            store_disposal_rate_quantity,
            do_avg_daily_quantity, do_avg_daily_sales,
            do_adoption_rate, do_out_of_stock_rate_hours,
            do_disposal_rate_quantity,
            start_date
          FROM read_parquet('${storeFileName}')
          WHERE ${storeWhere.join("\n            AND ")}
        ) AS s
        INNER JOIN read_parquet('${clusterFileName}') AS c
          ON s.item_cd = c.item_cd
         AND s.start_date = c.start_date
        LEFT JOIN read_parquet('${stockFileName}') AS stk
          ON CAST(s.item_cd AS VARCHAR) = CAST(stk.item_cd AS VARCHAR)
      ),
      top_items AS (
        SELECT *
        FROM joined
        QUALIFY ROW_NUMBER() OVER (
          PARTITION BY store_cd
          ORDER BY has_wsi DESC,
                   ${sortConfig.qualifyCol} DESC NULLS LAST,
                   CAST(item_cd AS VARCHAR),
                   store_cd
        ) <= 100
      )
      SELECT
        '${tenpoCode}' AS tenpo_code,
        TRIM(CAST(store_name AS VARCHAR)) AS tenpo_name,
        CAST(default_cluster AS VARCHAR) AS org_cluster_no,
        selected_cluster AS cluster_no,
        CAST(do_cd AS VARCHAR) AS do_code,
        TRIM(CAST(do_name AS VARCHAR)) AS do_name,
        CAST(item_cd AS VARCHAR) AS item_code,
        TRIM(CAST(item_name AS VARCHAR)) AS item_name,
        TRIM(CAST(pma AS VARCHAR)) AS pma_code,
        TRIM(CAST(pma_name AS VARCHAR)) AS pma_name,
        TRIM(CAST(information_class AS VARCHAR)) AS info_class_code,
        TRIM(CAST(information_class_name AS VARCHAR)) AS info_class_name,
        tax_included_price AS item_price,
        item_rank AS basic_item,
        stock_amnt AS stock_amount,
        cluster_avg_recommended_stores_per_day,
        TRIM(CAST(store_new_item_flg AS VARCHAR)) AS store_new_item_flg,
        TRIM(CAST(recommendation AS VARCHAR)) AS recommendation,
        CAST(order_start_date AS VARCHAR) AS order_start_date,
        CAST(order_end_date AS VARCHAR) AS order_end_date,
        RANK() OVER (PARTITION BY store_cd
          ORDER BY store_avg_daily_quantity DESC NULLS LAST) AS sales_quantity_rank,
        RANK() OVER (PARTITION BY store_cd
          ORDER BY store_avg_daily_sales DESC NULLS LAST) AS sales_amount_rank,
        RANK() OVER (PARTITION BY selected_cluster
          ORDER BY cluster_avg_daily_quantity DESC NULLS LAST) AS cluster_sales_quantity_rank,
        RANK() OVER (PARTITION BY selected_cluster
          ORDER BY cluster_avg_daily_sales DESC NULLS LAST) AS cluster_sales_amount_rank,
        RANK() OVER (PARTITION BY do_cd
          ORDER BY do_avg_daily_quantity DESC NULLS LAST) AS do_sales_quantity_rank,
        RANK() OVER (PARTITION BY do_cd
          ORDER BY do_avg_daily_sales DESC NULLS LAST) AS do_sales_amount_rank,
        cluster_adoption_rate AS adoption_rate_similar_stores,
        do_adoption_rate AS adoption_rate_do,
        store_avg_daily_quantity AS average_sales_quantity,
        store_avg_daily_sales AS average_sales_amount,
        store_out_of_stock_rate_hours AS total_out_of_stock_hours,
        store_disposal_rate_quantity AS average_disposal_quantity_rate,
        cluster_avg_daily_quantity AS cluster_average_sales_quantity,
        cluster_avg_daily_sales AS cluster_average_sales_amount,
        cluster_out_of_stock_rate_hours AS cluster_total_out_of_stock_hours,
        cluster_disposal_rate_quantity AS cluster_average_disposal_quantity_rate,
        do_avg_daily_quantity AS do_average_sales_quantity,
        do_avg_daily_sales AS do_average_sales_amount,
        do_out_of_stock_rate_hours AS do_total_out_of_stock_hours,
        do_disposal_rate_quantity AS do_average_disposal_quantity_rate,
        SPLIT_PART(CAST(stock_amnt_datetime AS VARCHAR), '+', 1) AS stock_amount_datetime
      FROM top_items
      ORDER BY ${sortConfig.rankAlias} ${sortDir}
    `;

    const needsFilterRefresh = !cachedTenpoItemData || cachedTenpoItemData.storePath !== storePath;

    const filtersSql = needsFilterRefresh ? `
      SELECT DISTINCT
        TRIM(CAST(pma AS VARCHAR)) AS combined_code_pma,
        TRIM(CAST(pma_name AS VARCHAR)) AS pma_name,
        TRIM(CAST(information_class AS VARCHAR)) AS combined_code_info,
        TRIM(CAST(information_class_name AS VARCHAR)) AS info_class_name,
        CAST(daily AS VARCHAR) AS daily,
        CAST(non_daily AS VARCHAR) AS non_daily
      FROM read_parquet('${storeFileName}')
      WHERE pma IS NOT NULL
        AND TRIM(CAST(pma AS VARCHAR)) != ''
        AND information_class IS NOT NULL
        AND TRIM(CAST(information_class AS VARCHAR)) != ''
      ORDER BY 1, 3
    ` : null;

    console.time(`${tag} SQL実行`);
    const [mainResult, filtersResult] = await Promise.all([
      conn.query(mainSql),
      filtersSql ? (async () => {
        const c2 = await db.connect();
        try { return await c2.query(filtersSql); }
        finally { await c2.close(); }
      })() : Promise.resolve(null),
    ]);
    console.timeEnd(`${tag} SQL実行`);

    console.time(`${tag} データ変換`);
    const assortmentAnalysis = mainResult.toArray().map((row: any) => ({
      ...row,
      tenpo_name: String(row.tenpo_name ?? "").trim(),
      item_name: String(row.item_name ?? "").trim(),
      info_class_name: String(row.info_class_name ?? "").trim(),
      org_cluster_no: String(row.org_cluster_no ?? "").padStart(2, "0"),
      cluster_no: String(row.cluster_no ?? "").padStart(2, "0"),
      item_price: Number(row.item_price),
      average_sales_amount: Number(row.average_sales_amount) / 1000,
      cluster_average_sales_amount: Number(row.cluster_average_sales_amount) / 1000,
      do_average_sales_amount: Number(row.do_average_sales_amount) / 1000,
      sales_quantity_rank: Number(row.sales_quantity_rank),
      sales_amount_rank: Number(row.sales_amount_rank),
      cluster_sales_quantity_rank: Number(row.cluster_sales_quantity_rank),
      cluster_sales_amount_rank: Number(row.cluster_sales_amount_rank),
      do_sales_quantity_rank: Number(row.do_sales_quantity_rank),
      do_sales_amount_rank: Number(row.do_sales_amount_rank),
    }));

    let tenpoItemData: any[];
    if (filtersResult) {
      tenpoItemData = filtersResult.toArray().map((row: any) => ({
        combined_code: `${row.combined_code_pma}_${row.combined_code_info}`,
        pma_name: String(row.pma_name ?? "").trim(),
        info_class_name: String(row.info_class_name ?? "").trim(),
        daily: String(row.daily ?? ""),
        non_daily: String(row.non_daily ?? ""),
      }));
      cachedTenpoItemData = { storePath, data: tenpoItemData };
    } else {
      tenpoItemData = cachedTenpoItemData!.data;
    }
    console.timeEnd(`${tag} データ変換`);

    console.timeEnd(`${tag} 処理合計`);
    return {
      assortmentAnalysis,
      tenpoItemData,
      defaultCluster: paddedCluster,
    };
  } finally {
    await conn.close();
  }
};
